import { useState, useRef, useEffect } from "react";
import { api, apiError, retryRequest } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MultiCombobox } from "@/components/wizard/MultiCombobox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2, AlertTriangle, ArrowRight, Trash2, FileUp, Users, XCircle, Bookmark, Plus } from "lucide-react";
import { GeoTiffLayer } from "@/components/GeoTiffLayer";

export default function Step2Areas({ project, catalog, reload, onNext }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [holderAll, setHolderAll] = useState("");
  const [splitMode, setSplitMode] = useState("combined");
  const [dup, setDup] = useState(null); // { files, duplicates }
  const inputRef = useRef(null);
  const areas = project.areas || [];
  const validated = areas.filter((a) => a.status !== "error");
  const [presets, setPresets] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [pName, setPName] = useState("");
  const [pMinerals, setPMinerals] = useState([]);
  const [confirmPreset, setConfirmPreset] = useState(null);

  useEffect(() => { api.get("/me/commodity-presets").then((r) => setPresets(r.data || [])).catch(() => {}); }, []);

  const savePreset = async () => {
    const name = pName.trim();
    if (!name) return toast.error("Name your preset");
    if (!pMinerals.length) return toast.error("Pick at least one commodity");
    try {
      const { data } = await api.post("/me/commodity-presets", { name, minerals: pMinerals });
      setPresets(data || []); setShowSave(false); setPName(""); setPMinerals([]);
      toast.success(`Preset "${name}" saved`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const deletePreset = async (name) => {
    try { const { data } = await api.delete(`/me/commodity-presets?name=${encodeURIComponent(name)}`); setPresets(data || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  const applyPresetToAll = async (preset) => {
    if (!preset) return;
    try {
      const { data } = await api.post(`/projects/${project.id}/areas/apply-minerals`, { minerals: preset.minerals });
      toast.success(`Applied "${preset.name}" to ${data.applied} licence(s)`);
      await reload();
    } catch (e) { toast.error(apiError(e)); }
    finally { setConfirmPreset(null); }
  };

  const commodityOptions = catalog?.commodity_profiles
    ? Object.entries(catalog.commodity_profiles).map(([k, v]) => ({ value: k, label: v.label || k }))
    : (catalog?.commodities || []).map((c) => ({ value: c, label: c }));

  const upload = async (files, { force = false } = {}) => {
    const list = Array.from(files || []).filter((f) => /\.(kml|kmz|geojson|zip)$/i.test(f.name));
    if (!list.length) return toast.error("Please upload one or more .KML / .KMZ files");
    setBusy(true);
    const fd = new FormData();
    list.forEach((f) => fd.append("files", f));
    fd.append("split", splitMode);
    fd.append("force", force ? "true" : "false");
    try {
      const { data } = await retryRequest(() => api.post(`/projects/${project.id}/areas/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }), { retries: 1, delay: 2000 });
      const duplicates = data?.duplicates || [];
      if (data?.added) toast.success(`${data.added} area(s) uploaded & validated`);
      if (duplicates.length && !force) {
        const dupNames = new Set(duplicates.map((d) => d.filename));
        // Keep ONLY the duplicate files so a "force" re-upload doesn't re-add the others.
        setDup({ files: list.filter((f) => dupNames.has(f.name)), duplicates });
      } else {
        setDup(null);
        if (!data?.added) toast.info("No new areas added");
      }
      const mp = data?.multi_polygon || [];
      if (mp.length && splitMode === "combined") {
        toast.info(`${mp.length} file(s) contained multiple polygons - kept as one combined area each. Switch to "Separate areas" above to split them.`);
      }
      await reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const applyHolderToAll = async () => {
    if (!holderAll.trim()) return toast.error("Enter a licence holder to apply");
    try {
      await api.post(`/projects/${project.id}/areas/apply-holder`, { licence_holder: holderAll.trim() });
      toast.success("Licence holder applied to all areas");
      await reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="step2-areas">
      <AlertDialog open={!!confirmPreset} onOpenChange={(o) => { if (!o) setConfirmPreset(null); }}>
        <AlertDialogContent data-testid="preset-apply-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Bookmark size={18} className="text-primary" /> Apply "{confirmPreset?.name}" to every licence?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces the commodities on all {validated.length} licence(s) with
              {" "}<span className="font-semibold text-foreground">{(confirmPreset?.minerals || []).map((m) => (commodityOptions.find((o) => o.value === m)?.label) || m).join(", ")}</span>.
              Any commodities you selected per licence will be overwritten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="preset-apply-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="preset-apply-confirm-btn" onClick={() => applyPresetToAll(confirmPreset)}>Replace on all licences</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div>
        <h2 className="text-lg font-bold text-foreground tracking-tight">Upload Project Areas</h2>
        <p className="text-sm text-muted-foreground">Upload licence boundaries (KML / KMZ). For each one, just confirm the <b>Licence Number</b> and <b>Licence Holder</b>.</p>
      </div>

      <div className="flex items-center gap-2 text-xs" data-testid="split-mode-toggle">
        <span className="text-muted-foreground font-semibold uppercase tracking-wider text-[10px]">Multi-polygon files:</span>
        {[["combined", "One combined area"], ["separate", "Separate area per polygon"]].map(([v, label]) => (
          <button key={v} data-testid={`split-${v}`} onClick={() => setSplitMode(v)}
            className={`px-3 py-1 rounded-full border font-semibold transition-colors ${splitMode === v ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/50"}`}>
            {label}
          </button>
        ))}
      </div>

      <div
        data-testid="areas-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`h-56 rounded-lg border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors
          ${drag ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/60 hover:bg-primary/5"}`}
      >
        <input ref={inputRef} type="file" accept=".kml,.kmz,.geojson,.zip" multiple className="hidden"
          data-testid="areas-file-input" onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; upload(fs); }} />
        <UploadCloud size={44} className={drag ? "text-primary" : "text-muted-foreground"} />
        <p className="mt-4 text-foreground font-semibold tracking-tight">
          {busy ? "Uploading & validating…" : "DROP KML / KMZ FILES HERE"}
        </p>
        <p className="text-sm text-muted-foreground mt-1">or click to browse - you can select several licence boundaries at once</p>
      </div>

      {dup && (
        <div className="bg-red-50 border border-red-300 rounded-xl p-4" data-testid="dup-warning">
          <div className="flex items-center gap-2 text-red-800 font-bold text-sm">
            <AlertTriangle size={16} /> Duplicate boundary detected
          </div>
          <p className="text-xs text-red-800/90 mt-1">
            {dup.duplicates.length} uploaded boundary(ies) already exist in this project
            ({dup.duplicates.map((d) => d.existing_area_id).join(", ")}). You can reuse the existing
            area(s) and add minerals to them, or deliberately create a separate analysis run.
          </p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" data-testid="dup-cancel" onClick={() => setDup(null)}
              className="h-8 text-xs bg-white text-red-900 border border-red-400 hover:bg-red-100 hover:text-red-900">Keep existing (cancel)</Button>
            <Button size="sm" data-testid="dup-force" disabled={busy}
              onClick={() => upload(dup.files, { force: true })}
              className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white">
              Create separate run anyway
            </Button>
          </div>
        </div>
      )}

      {areas.length > 1 && (
        <div className="bg-muted border border-border rounded-xl p-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="apply-holder-bar">
          <div className="flex-1">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
              <Users size={13} /> Licence Holder for all areas
            </label>
            <Input data-testid="holder-all-input" value={holderAll} onChange={(e) => setHolderAll(e.target.value)}
              placeholder="e.g. Shanvi Resources Ltd" className="mt-1 h-10 bg-card" />
          </div>
          <Button data-testid="apply-holder-all-btn" onClick={applyHolderToAll}
            className="bg-primary hover:bg-primary/90 text-primary-foreground h-10 px-5 text-sm font-semibold">
            Apply to all
          </Button>
        </div>
      )}

      {areas.length > 0 && (
        <div className="bg-muted border border-border rounded-xl p-4 space-y-3" data-testid="commodity-presets-bar">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
              <Bookmark size={13} /> Commodity Presets
            </label>
            <Button size="sm" variant="outline" data-testid="preset-add-btn" onClick={() => setShowSave((v) => !v)}
              className="h-8 text-xs border-border">
              <Plus size={13} className="mr-1" /> Save preset
            </Button>
          </div>
          {showSave && (
            <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end bg-card border border-border rounded-lg p-3" data-testid="preset-save-form">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Preset name</label>
                <Input data-testid="preset-name-input" value={pName} onChange={(e) => setPName(e.target.value)}
                  placeholder="e.g. Base metals" className="mt-1 h-9 bg-card" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Commodities</label>
                <div className="mt-1">
                  <MultiCombobox testid="preset-minerals" options={commodityOptions} value={pMinerals}
                    placeholder="Select commodities…" onChange={setPMinerals} />
                </div>
              </div>
              <Button data-testid="preset-save-btn" onClick={savePreset}
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-9 px-4 text-sm font-semibold">Save</Button>
            </div>
          )}
          {presets.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="preset-chips">
              {presets.map((pre) => (
                <div key={pre.name} data-testid={`preset-chip-${pre.name}`}
                  className="inline-flex items-center gap-2 rounded-full bg-card border border-primary/30 pl-3 pr-1.5 py-1 text-xs">
                  <span className="font-semibold text-foreground">{pre.name}</span>
                  <span className="text-muted-foreground">{(pre.minerals || []).length}</span>
                  <button type="button" data-testid={`preset-apply-all-${pre.name}`} onClick={() => setConfirmPreset(pre)}
                    className="rounded-full bg-primary/10 text-primary hover:bg-primary/20 px-2 py-0.5 font-semibold">Apply to all</button>
                  <button type="button" data-testid={`preset-del-${pre.name}`} onClick={() => deletePreset(pre.name)}
                    className="text-muted-foreground hover:text-red-600"><XCircle size={14} /></button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Save a favourite set of commodities and apply it to any licence in one tap.</p>
          )}
        </div>
      )}

      {areas.length > 0 && (
        <div className="space-y-3" data-testid="areas-list">
          {areas.map((a) => (
            <AreaRow key={a.area_id} project={project} area={a} reload={reload}
              commodityOptions={commodityOptions} presets={presets} />
          ))}
        </div>
      )}

      {areas.length === 0 && !busy && (
        <p className="text-sm text-muted-foreground flex items-center gap-2"><FileUp size={15} />Upload at least one licence boundary to continue.</p>
      )}

      {areas.length > 0 && (
        <GeoTiffLayer pid={project.id} title="Reference GeoTIFF Layer (optional)" />
      )}

      <div className="flex justify-between items-center pt-2">
        <div className="text-sm text-muted-foreground" data-testid="areas-count">
          {validated.length} valid area(s){areas.length - validated.length > 0 ? ` · ${areas.length - validated.length} error(s)` : ""}
        </div>
        <Button data-testid="areas-continue" onClick={onNext} disabled={validated.length === 0}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-sm font-semibold disabled:opacity-40">
          Next: Reference &amp; Geology <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}

function AreaRow({ project, area, reload, commodityOptions, presets = [] }) {
  const [presetOpen, setPresetOpen] = useState(false);
  const [name, setName] = useState(area.name || "");
  const [holder, setHolder] = useState(area.licence_holder || "");
  const [minerals, setMinerals] = useState(area.minerals || []);
  const aFirst = useRef(true);
  const aTimer = useRef(null);
  useEffect(() => { setName(area.name || ""); }, [area.name]);
  useEffect(() => { setHolder(area.licence_holder || ""); }, [area.licence_holder]);
  useEffect(() => { setMinerals(area.minerals || []); }, [area.minerals]);

  // Debounced silent autosave so licence fields typed in Step 2 are never lost.
  useEffect(() => {
    if (aFirst.current) { aFirst.current = false; return; }
    if (aTimer.current) clearTimeout(aTimer.current);
    aTimer.current = setTimeout(() => {
      const patch = {};
      if (name !== (area.name || "")) patch.name = name;
      if (holder !== (area.licence_holder || "")) patch.licence_holder = holder;
      if (Object.keys(patch).length) {
        retryRequest(() => api.patch(`/projects/${project.id}/areas/${area.area_id}`, patch), { retries: 1, delay: 2000 })
          .then(() => { try { window.dispatchEvent(new CustomEvent("sgex:area-saved")); } catch (_) { /* noop */ } })
          .catch(() => {});
      }
    }, 1000);
    return () => { if (aTimer.current) clearTimeout(aTimer.current); };
  }, [name, holder]);
  const err = area.status === "error";
  const m = area.boundary_metrics || {};

  const save = async (patch) => {
    try { await api.patch(`/projects/${project.id}/areas/${area.area_id}`, patch); await reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  // Multi Commodity picker: persist the licence's target minerals (the full set analysed on Generate).
  // Optimistic + no full reload so the chip list stays snappy while toggling.
  const saveMinerals = async (next) => {
    setMinerals(next);
    try {
      await api.patch(`/projects/${project.id}/areas/${area.area_id}`, { minerals: next, commodity: next[0] || null });
      try { window.dispatchEvent(new CustomEvent("sgex:area-saved")); } catch (_) { /* noop */ }
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    try { await api.delete(`/projects/${project.id}/areas/${area.area_id}`); toast.success(`${area.area_id} removed`); await reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className={`bg-card border rounded-xl shadow-sm p-4 ${err ? "border-red-200" : "border-border"}`} data-testid={`area-row-${area.area_id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-11 w-11 rounded-lg bg-primary text-primary-foreground grid place-items-center font-extrabold text-sm shrink-0 mt-1">
          {area.area_id}
        </div>

        <div className="min-w-0 flex-1 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Licence / Project Number</label>
            <Input data-testid={`area-name-${area.area_id}`} value={name} onChange={(e) => setName(e.target.value)}
              onBlur={() => name !== area.name && save({ name })}
              className="mt-1 h-9 text-sm font-semibold text-foreground" placeholder="e.g. PL 12345/2026" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Licence Holder</label>
            <Input data-testid={`area-holder-${area.area_id}`} value={holder} onChange={(e) => setHolder(e.target.value)}
              onBlur={() => holder !== (area.licence_holder || "") && save({ licence_holder: holder })}
              className="mt-1 h-9 text-sm text-foreground" placeholder="e.g. Shanvi Resources Ltd" />
          </div>
          <div className="truncate text-[11px] text-muted-foreground sm:col-span-2">{area.filename}</div>
        </div>

        <div className="flex flex-col items-end gap-2">
          {!err ? (
            <div className="flex items-center gap-1.5 text-emerald-600 text-sm font-semibold" data-testid={`area-status-${area.area_id}`}>
              <CheckCircle2 size={17} /> Validated
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-red-600 text-sm font-semibold" data-testid={`area-status-${area.area_id}`}>
              <AlertTriangle size={17} /> {area.error || "Validation error"}
            </div>
          )}
          {!err && (
            <div className="w-52" data-testid={`area-commodity-block-${area.area_id}`}>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1 text-right">Multi Commodity</label>
              <MultiCombobox testid={`area-commodity-${area.area_id}`}
                options={commodityOptions} value={minerals}
                placeholder="Select commodities…" onChange={saveMinerals} />
              {minerals.length > 0 && (
                <div className="flex flex-wrap gap-1 justify-end mt-1.5" data-testid={`area-commodity-chips-${area.area_id}`}>
                  {minerals.map((m) => {
                    const lab = (commodityOptions.find((o) => o.value === m)?.label) || m;
                    return (
                      <span key={m} data-testid={`area-commodity-chip-${area.area_id}-${m}`}
                        className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary border border-primary/30 px-2 py-0.5 text-[11px] font-semibold">
                        {lab}
                        <button type="button" onClick={() => saveMinerals(minerals.filter((x) => x !== m))}
                          data-testid={`area-commodity-rm-${area.area_id}-${m}`} className="hover:text-red-600"><XCircle size={11} /></button>
                      </span>
                    );
                  })}
                </div>
              )}
              {presets.length > 0 && (
                <Popover open={presetOpen} onOpenChange={setPresetOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid={`area-preset-btn-${area.area_id}`}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-semibold">
                      <Bookmark size={11} /> Apply preset
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-52 p-1 z-[1200]">
                    {presets.map((pre) => (
                      <button key={pre.name} type="button" data-testid={`area-preset-opt-${area.area_id}-${pre.name}`}
                        onClick={() => { saveMinerals(pre.minerals); setPresetOpen(false); toast.success(`Applied "${pre.name}"`); }}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-sm flex items-center justify-between">
                        <span className="font-medium text-foreground truncate">{pre.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">{(pre.minerals || []).length}</span>
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}
          <button onClick={del} data-testid={`area-delete-${area.area_id}`}
            className="h-8 px-2 flex items-center gap-1 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 text-xs">
            <Trash2 size={15} /> Remove
          </button>
        </div>
      </div>

      {!err && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-border text-xs">
          <Stat label="Licence Area" value={`${m.area_km2 ?? "-"} km²`} />
          <Stat label="Centroid" value={m.centroid ? `${m.centroid.lat}, ${m.centroid.lon}` : "-"} />
          <Stat label="CRS / UTM" value={`WGS 84 · ${m.utm_zone || area.utm_zone || "-"}`} />
          <Stat label="Auto-Fix" value={area.auto_fixed || "None"} />
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
    <div className="text-foreground font-semibold mt-0.5 truncate">{value}</div>
  </div>
);
